import React, { useCallback, useMemo } from 'react'
import {
  useGetDocumentsListByProjectQuery,
  useGetUserProfileQuery,
} from '../../redux/api-slice'
import { skipToken } from '@reduxjs/toolkit/dist/query'
import { useDispatch, useSelector } from 'react-redux'
import {
  selectCurrentProject,
  selectSupplementaryConditionsAreProcessing,
  setModal,
} from '../../redux/application-slice'
import { Loader, Text } from '@mantine/core'
import {
  ClipboardDocumentCheckIcon,
  DocumentCheckIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline'
import { MODAL_TYPES } from '../modals/modal-controller'

const ProjectUploadStatus: React.FC = () => {
  const currentProject = useSelector(selectCurrentProject)
  const dispatch = useDispatch()
  const supplementaryConditionsAreProcessing = useSelector(
    selectSupplementaryConditionsAreProcessing
  )

  const { currentData: documents } = useGetDocumentsListByProjectQuery(
    currentProject ? { projectId: currentProject?.id } : skipToken,
    {
      pollingInterval: 5000,
    }
  )
  const { data: user } = useGetUserProfileQuery(undefined)

  const documentProcessingCount = useMemo(() => {
    if (!documents) {
      return 0
    }

    return documents.filter(
      (document) =>
        document.job_status === 'PROCESSING' ||
        document.job_status === 'PENDING'
    ).length
  }, [documents])

  const documentErrorCount = useMemo(() => {
    if (!documents) {
      return 0
    }

    return documents.filter((document) => document.job_status === 'ERROR')
      .length
  }, [documents])

  const hasScAndTemplate = useMemo(() => {
    return (
      documents?.some((document) =>
        document.classifications?.some(
          (classification) =>
            classification.classification === 'SUPPLEMENTARY_CONDITIONS'
        )
      ) &&
      documents?.some((document) =>
        document.classifications?.some(
          (classification) =>
            classification.classification === 'TEMPLATE_DOCUMENT'
        )
      )
    )
  }, [documents])

  const getStatusDescription = useMemo(() => {
    if (documentProcessingCount > 0) {
      return (
        <Text size="sm" className="flex items-center gap-2">
          <div className="flex h-5 w-5 items-center justify-center">
            <Loader size="xs" />
          </div>
          {documentProcessingCount} document
          {documentProcessingCount > 1 ? 's' : ''} processing
        </Text>
      )
    }

    if (supplementaryConditionsAreProcessing) {
      return (
        <Text size="sm" className="flex items-center gap-2">
          <ClipboardDocumentCheckIcon className="h-5 w-5 text-gray-400" />
          Supplementary conditions are being reviewed
        </Text>
      )
    }

    if (documentErrorCount > 0) {
      return (
        <Text size="sm" className="flex items-center gap-2">
          <ExclamationCircleIcon className="h-5 w-5 text-red-500" />
          {documentErrorCount} document
          {documentErrorCount > 1 ? 's have an error' : ' has an error'}
        </Text>
      )
    }

    return (
      <Text size="sm" className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <DocumentCheckIcon className="h-5 w-5 text-gray-400" />
          <div>Documents complete</div>
        </div>
      </Text>
    )
  }, [
    documentProcessingCount,
    supplementaryConditionsAreProcessing,
    documentErrorCount,
    user?.feature_flags?.check_for_supplementary_conditions,
    hasScAndTemplate,
    documents?.length,
  ])

  return <div className="border-b p-2">{getStatusDescription}</div>
}

export default ProjectUploadStatus
